/**
 * Module 'asks' (ADR 0056): what a session that ended its turn waiting on a person asked, in
 * full. Carried on the session report as `session.ext.asks` (no core field); a structured ask
 * (a tool, question, plan or elicitation approval) is an ApprovalRequest instead.
 *
 * Both agents fill it from the same text with `sessionAskOf`, so the Go agent's port is held
 * to this file by a parity test (agents/node-agent/internal/protocol/asks_parity_test.go).
 */
import type { Id, Session, Ts } from '../index.ts';
import { SESSION_LAST_TEXT_MAX, clip, clipTail, clipText } from '../index.ts';

/** The whole last assistant message is kept up to this many chars; a longer one keeps its END. */
export const SESSION_ASK_TEXT_MAX = 8000;
/** Quick replies offered from the choices a message lists. */
export const SESSION_ASK_OPTIONS_MAX = 6;
/** A list item longer than this is prose, not a choice: such a list offers no quick replies. */
export const SESSION_ASK_OPTION_MAX = 160;
/** The question line kept for rows, notifications and the card. */
export const SESSION_ASK_QUESTION_MAX = 600;

/** Where the ask was read from: Claude Code's Stop hook, the Orgus mod, or the transcript. */
export type SessionAskSource = 'stop-hook' | 'mod' | 'transcript';

export interface SessionAsk {
  /** The session id and the uuid of the assistant message that asks. */
  id: Id;
  sessionId: Id;
  /** The whole last assistant message, Markdown, at most SESSION_ASK_TEXT_MAX chars (its end). */
  text: string;
  /** Its closing question (the last sentence ending in '?'), else its last paragraph. */
  question: string | null;
  /** Choices the message lists (numbered or bulleted) at its end, for quick replies. */
  options: string[];
  askedAt: Ts;
  source: SessionAskSource;
}

/** The session's turn-end ask from its report, or null (older agents, or nothing asked). */
export function sessionAskOf(ext: Record<string, unknown> | undefined): SessionAsk | null {
  const a = ext?.asks;
  if (!a || typeof a !== 'object' || Array.isArray(a)) return null;
  const x = a as Partial<SessionAsk>;
  if (typeof x.text !== 'string' || typeof x.id !== 'string') return null;
  return {
    id: x.id,
    sessionId: typeof x.sessionId === 'string' ? x.sessionId : '',
    text: x.text,
    question: typeof x.question === 'string' ? x.question : null,
    options: Array.isArray(x.options) ? x.options.filter((o): o is string => typeof o === 'string') : [],
    askedAt: typeof x.askedAt === 'number' ? x.askedAt : 0,
    source: x.source === 'stop-hook' || x.source === 'mod' ? x.source : 'transcript',
  };
}

/** A top-level list item: `-`, `*`, `+`, `1.`, `1)`, `a.` or `a)`, then its text. */
const LIST_ITEM = /^ {0,3}(?:[-*+]|\d{1,2}[.)]|[A-Za-z][.)])[ \t]+(\S.*)$/;
/** A line indented under a list item (its wrapped text or a nested list). */
const CONTINUATION = /^(?: {2,}|\t)\S/;
/** Markdown emphasis and code marks, dropped from a quick reply. */
const MARKS = /\*\*|__|`/g;
/** What may close a question after its '?': emphasis, code, quotes, brackets. */
const CLOSERS = '*_`)"\']';

const isBlank = (l: string) => l.trim() === '';

/** The choices listed at the end of the text: the last list, followed by at most one paragraph. */
function trailingOptions(lines: string[]): string[] {
  let i = lines.length - 1;
  let paras = 0;
  let inPara = false;
  for (; i >= 0; i--) {
    const l = lines[i]!;
    if (isBlank(l)) {
      inPara = false;
      continue;
    }
    if (LIST_ITEM.test(l)) break;
    if (!inPara) {
      paras++;
      inPara = true;
      if (paras > 1) return [];
    }
  }
  if (i < 0) return [];
  const items: string[] = [];
  for (; i >= 0; i--) {
    const l = lines[i]!;
    if (isBlank(l) || (CONTINUATION.test(l) && !LIST_ITEM.test(l))) continue;
    const m = LIST_ITEM.exec(l);
    if (!m) break;
    items.unshift(m[1]!.replace(MARKS, '').replace(/\s+/g, ' ').trim());
  }
  if (items.length < 2 || items.some((x) => !x || x.length > SESSION_ASK_OPTION_MAX)) return [];
  return items.slice(0, SESSION_ASK_OPTIONS_MAX);
}

/** The last paragraph of prose (list items and their indented lines left out), on one line. */
function lastProse(lines: string[]): string {
  let para: string[] = [];
  let last = '';
  const flush = () => {
    const p = para.join(' ').replace(/\s+/g, ' ').trim();
    if (p) last = p;
    para = [];
  };
  let inItem = false;
  for (const l of lines) {
    if (isBlank(l)) {
      flush();
      inItem = false;
      continue;
    }
    if (LIST_ITEM.test(l)) {
      inItem = true;
      continue;
    }
    if (inItem && CONTINUATION.test(l)) continue;
    inItem = false;
    para.push(l);
  }
  flush();
  return last;
}

/** The last sentence of `p` that ends in '?' (with what closes it), or null. */
function lastQuestion(p: string): string | null {
  const q = p.lastIndexOf('?');
  if (q < 0) return null;
  let end = q + 1;
  while (end < p.length && CLOSERS.includes(p[end]!)) end++;
  let start = 0;
  for (let i = q - 1; i > 0; i--) {
    if (p[i] === ' ' && '.!?:'.includes(p[i - 1]!)) {
      start = i + 1;
      break;
    }
  }
  const s = p.slice(start, end).trim();
  return s || null;
}

/**
 * What a message asks: its closing question (the last sentence ending in '?' in its last
 * paragraph of prose, else that paragraph) and the choices it lists at its end. Pure.
 */
export function askFromText(text: string): { question: string | null; options: string[] } {
  const lines = text.replace(/\r\n?/g, '\n').trim().split('\n');
  const p = lastProse(lines);
  const q = p ? (lastQuestion(p) ?? p) : null;
  return { question: q ? clip(q, SESSION_ASK_QUESTION_MAX) : null, options: trailingOptions(lines) };
}

/** The SessionAsk for a session's last assistant message (both agents build it this way). */
export function sessionAsk(sessionId: Id, messageId: string | null, text: string, askedAt: Ts, source: SessionAskSource): SessionAsk {
  const kept = clipText(text, SESSION_ASK_TEXT_MAX, 'tail');
  const { question, options } = askFromText(text);
  return { id: `${sessionId}:${messageId || askedAt}`, sessionId, text: kept, question, options, askedAt, source };
}

/**
 * Blocked is not the same as asked (ADR 0056): 'blocked' when the session cannot go on (a tool
 * permission, a question tool, a plan approval or an MCP form is held open: Needs you, amber);
 * 'asked' when its turn ended on a question and nothing is held (neutral, with the question);
 * null otherwise. `held` is whether an approval of the session is pending.
 */
export type AskLevel = 'blocked' | 'asked';

export function askLevel(s: Pick<Session, 'status' | 'ext' | 'lastText'>, held: boolean): AskLevel | null {
  if (s.status === 'ended') return null;
  if (held) return 'blocked';
  if (s.status !== 'waiting') return null;
  return sessionAskOf(s.ext) || /\?\s*$/.test(s.lastText) ? 'asked' : 'blocked';
}

/** A message this short is a row's whole line; a longer one gives the row its question. */
const ASK_LINE_WHOLE_MAX = 120;

/** A question as askFromText found it, ending in '?' and what may close it. */
const ENDS_ASKING = /\?[*_`)"'\]]*$/;

/**
 * The one-line `Session.lastText` of a message that ends asking (ADR 0056): a short message
 * whole, else its closing question, else its end, so a row, the phone and a notification read
 * the question and not the head of a long message.
 */
export function askLastText(text: string): string {
  const whole = clip(text, Infinity);
  if (whole.length <= ASK_LINE_WHOLE_MAX) return whole;
  const { question } = askFromText(text);
  return clipTail(question && ENDS_ASKING.test(question) ? question : text, SESSION_LAST_TEXT_MAX);
}

// ---------------------------------------------------------------------------
// Dismiss and snooze (ADR 0056): hub module 'asks', state.ext.asks
// ---------------------------------------------------------------------------

/** An asked question unanswered this long reads "Open question" under Idle and stops counting. */
export const ASK_OPEN_QUESTION_MS = 24 * 60 * 60_000;
/** How long the hub keeps a dismissal or snooze (the Inbox's Done tab lists dismissals this long). */
export const ASK_SET_ASIDE_KEEP_MS = 7 * 24 * 60 * 60_000;
/** At most this many set-aside asks are kept (oldest go first). */
export const ASK_SET_ASIDE_MAX = 500;

/**
 * A person set an asked question aside: dismissed ("No answer needed") or snoozed. Per ask, so
 * the session's next question shows again. A blocked (held) ask is never set aside: it is handed
 * back or denied through approvals instead.
 */
export interface AskSetAside {
  askId: Id;
  sessionId: Id;
  kind: 'dismiss' | 'snooze';
  /** Snooze only: when it comes back; null until the session moves (asks something new). */
  until: Ts | null;
  by: string;
  /** Which device it came from ("Chrome on Windows"). */
  from: string;
  at: Ts;
  /** The question as it read then, for the Done list and the drawer (blank under content sharing off). */
  question: string | null;
}

/** state.ext.asks */
export interface AsksState {
  setAside: AskSetAside[];
}

/** POST /api/asks/:askId/dismiss, /snooze and /undo (operator+). */
export const ASKS_PATH = '/api/asks';
export type AskSetAsideAction = 'dismiss' | 'snooze' | 'undo';

/** Body of POST /api/asks/:askId/(dismiss|snooze). `until` is for snooze only (null: until the session moves). */
export interface AskSetAsideBody {
  sessionId: Id;
  until?: Ts | null;
}

export const ASKS_AUDIT_LABEL: Record<string, string> = {
  'ask.dismiss': 'Dismiss a question',
  'ask.snooze': 'Snooze a question',
  'ask.undo': 'Bring back a question',
};

/** The asks module's slice, tolerant of a hub without it. */
export function asksStateOf(ext: Record<string, unknown> | undefined): AsksState {
  const a = ext?.asks as Partial<AsksState> | undefined;
  return { setAside: Array.isArray(a?.setAside) ? a.setAside : [] };
}

/** FNV-1a, base 36: a short stable key for text. */
function textKey(t: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < t.length; i++) {
    h ^= t.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * The id a turn-end ask is set aside by: its SessionAsk id, else (an older agent) the session
 * and its last text, so a new message is a new ask.
 */
export function askIdOf(s: Pick<Session, 'id' | 'ext' | 'lastText'>): Id {
  return sessionAskOf(s.ext)?.id ?? `${s.id}:t${textKey(s.lastText)}`;
}

/** The set-aside in force for an ask now, or null: a dismissal always, a snooze until its time. */
export function setAsideFor(list: readonly AskSetAside[], askId: Id, now: Ts): AskSetAside | null {
  const x = list.find((a) => a.askId === askId);
  if (!x) return null;
  if (x.kind === 'snooze' && x.until != null && x.until <= now) return null;
  return x;
}
